/**
 * My Authorized Services Hook (provider-facing)
 *
 * The services the signed-in provider is authorized to offer — i.e. the only ones they
 * receive dispatches for.
 *
 * Deliberately NOT `GET /api/providers/<uuid>/services/`: that endpoint is guarded by
 * can_manage_provider_services(), which allows only staff, Operations, and the owning
 * company's manager. A provider reading their own set would get 403 PERMISSION_DENIED.
 * `me { providerProfile { capableServices } }` is caller-scoped and needs no such rights.
 */

import { useQuery } from '@tanstack/react-query'
import { graphqlQuery } from '@/services/graphql'

const MY_AUTHORIZED_SERVICES_QUERY = `
  query MyAuthorizedServices {
    me {
      providerProfile {
        capableServices {
          id
          name
          label
          basePrice
          estimatedDuration
        }
      }
    }
  }
`

export interface MyAuthorizedService {
  id: string
  name: string
  label?: string | null
  /** Decimal as string, or null. */
  basePrice?: string | null
  /** Int MINUTES (GraphQL), unlike the REST endpoint's float seconds. */
  estimatedDuration?: number | null
}

interface MyAuthorizedServicesResponse {
  me: {
    providerProfile: {
      capableServices: MyAuthorizedService[] | null
    } | null
  } | null
}

export function useMyAuthorizedServices(enabled = true) {
  const query = useQuery({
    queryKey: ['myAuthorizedServices'],
    queryFn: async () => {
      const response = await graphqlQuery<MyAuthorizedServicesResponse>(
        MY_AUTHORIZED_SERVICES_QUERY
      )
      return response.data?.me?.providerProfile?.capableServices ?? []
    },
    enabled,
    staleTime: 1000 * 60,
  })

  return {
    services: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
